import React, { Suspense } from 'react';
import { CenteredLoadingIndicator } from '@spotify-internal/anchor-ds';
import { detectLocale } from 'i18n/detectLocale';
import * as serverRenderingUtils from 'helpers/serverRenderingUtils';

type LocaleProviderValue = {
  changeLocale: (newLocale: string) => void;
  isI18nEnabled: boolean;
  locale: string;
};

export const LocaleContext = React.createContext<LocaleProviderValue>({
  changeLocale: () => {},
  isI18nEnabled: false,
  locale: 'en',
});

export function LocaleProvider({
  locale = 'en',
  children,
}: {
  locale?: string;
  children: React.ReactNode;
}): React.ReactElement {
  // TODO https://anchorfm.atlassian.net/browse/CAST-2547: Change isI18nEnabled variable to true always
  const developmentHostnames = ['localhost', 'development.anchor.fm'];
  const isI18nEnabled =
    !serverRenderingUtils.windowUndefined() &&
    developmentHostnames.includes(window.location.hostname);
  const [resolvedLocale, setResolvedLocale] = React.useState<string>(locale);

  const changeLocale = (requestedLocale: string) => {
    // Safety check to not run the changeLanguage function when unneccessary
    if (requestedLocale === resolvedLocale) {
      /* eslint-disable-next-line no-console */
      console.log('The preferred language is already set: ', resolvedLocale);
      return;
    }

    setResolvedLocale(requestedLocale);
  };

  React.useEffect(() => {
    // Only run this if isI18nEnbabled is true && we are not running unit tests. The unit test browser
    // is always set to 'en' so it will try to override whatever we pass to the LocaleProvider.
    // This does not effect cypress tests as the process.env.NODE_ENV is "development"
    if (isI18nEnabled && !process.env.NODE_ENV?.includes('test'))
      setResolvedLocale(detectLocale());
  }, [isI18nEnabled]);

  return (
    <LocaleContext.Provider
      value={{
        locale: resolvedLocale,
        isI18nEnabled,
        changeLocale,
      }}
    >
      <Suspense
        fallback={
          <div>
            <CenteredLoadingIndicator size="lg" />
          </div>
        }
      >
        {children}
      </Suspense>
    </LocaleContext.Provider>
  );
}
